import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  CreditCard,
  Loader2,
  X,
  Users,
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import SubscriptionBillingChecklist from '@/components/admin/SubscriptionBillingChecklist';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  interval: 'monthly' | 'annual';
  price: number;
  compare_at_price: number | null;
  features: string[];
  tier_level: number;
  courses_included: number | null;
  subscriber_count: number;
  is_active: boolean;
  created_at: string;
  stripe_price_id: string | null;
}

interface PlanFormData {
  name: string;
  description: string;
  interval: 'monthly' | 'annual';
  price: string;
  compare_at_price: string;
  features: string[];
  tier_level: string;
  courses_included: string;
  is_active: boolean;
}

const INITIAL_FORM: PlanFormData = {
  name: '',
  description: '',
  interval: 'monthly',
  price: '',
  compare_at_price: '',
  features: [],
  tier_level: '1',
  courses_included: '',
  is_active: true,
};

// Only the two intervals the whole stack actually speaks: the DB CHECK on
// subscription_plans.interval allows ('monthly','annual'), and sync-to-stripe
// maps 'annual'→year with everything else falling through to month. Offering
// Quarterly/Yearly here meant a 23514 on save — or, worse, a plan priced
// yearly but billed monthly in Stripe.
const intervalLabels: Record<string, string> = {
  monthly: 'Monthly',
  annual: 'Annual',
};

const intervalVariants: Record<string, 'blue' | 'green' | 'yellow'> = {
  monthly: 'blue',
  annual: 'yellow',
};

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form modal
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formData, setFormData] = useState<PlanFormData>(INITIAL_FORM);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newFeature, setNewFeature] = useState('');

  // Delete
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  async function fetchPlans() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ plans: SubscriptionPlan[] }>('/admin/subscription-plans');
      setPlans(response.plans);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription plans');
    } finally {
      setIsLoading(false);
    }
  }

  function openCreateModal() {
    setFormData(INITIAL_FORM);
    setEditingPlanId(null);
    setNewFeature('');
    setFormModalOpen(true);
  }

  function openEditModal(plan: SubscriptionPlan) {
    setFormData({
      name: plan.name,
      description: plan.description || '',
      interval: plan.interval,
      price: plan.price.toString(),
      compare_at_price: plan.compare_at_price?.toString() || '',
      features: Array.isArray(plan.features) ? [...plan.features] : [],
      tier_level: plan.tier_level.toString(),
      courses_included: plan.courses_included?.toString() || '',
      is_active: plan.is_active,
    });
    setEditingPlanId(plan.id);
    setNewFeature('');
    setFormModalOpen(true);
  }

  function addFeature() {
    if (!newFeature.trim()) return;
    setFormData((prev) => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
    setNewFeature('');
  }

  function removeFeature(index: number) {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  }

  async function handleSave() {
    if (!formData.name.trim() || !formData.price) {
      useUIStore.getState().addToast('Name and price are required', 'error');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        name: formData.name,
        description: formData.description,
        interval: formData.interval,
        price: parseFloat(formData.price),
        compare_at_price: formData.compare_at_price ? parseFloat(formData.compare_at_price) : null,
        features: formData.features,
        tier_level: parseInt(formData.tier_level) || 1,
        courses_included: formData.courses_included ? parseInt(formData.courses_included) : null,
        is_active: formData.is_active,
      };

      const res = editingPlanId
        ? await api.put<{ plan: SubscriptionPlan; stripe_sync_error?: string }>(
            `/admin/subscription-plans/${editingPlanId}`,
            payload,
          )
        : await api.post<{ plan: SubscriptionPlan; stripe_sync_error?: string }>(
            '/admin/subscription-plans',
            payload,
          );
      useUIStore
        .getState()
        .addToast(`Plan ${editingPlanId ? 'updated' : 'created'} successfully`, 'success');
      if (res?.stripe_sync_error) {
        useUIStore
          .getState()
          .addToast(`Saved, but Stripe sync failed: ${res.stripe_sync_error}`, 'error');
      }

      setFormModalOpen(false);
      fetchPlans();
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to save plan', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  function openDeleteModal(planId: string) {
    setDeletingPlanId(planId);
    setDeleteModalOpen(true);
  }

  async function handleDeactivate() {
    if (!deletingPlanId) return;

    try {
      setIsDeleting(true);
      await api.put(`/admin/subscription-plans/${deletingPlanId}`, { is_active: false });
      useUIStore.getState().addToast('Plan deactivated', 'success');
      setDeleteModalOpen(false);
      setDeletingPlanId(null);
      fetchPlans();
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to deactivate plan', 'error');
    } finally {
      setIsDeleting(false);
    }
  }

  async function togglePlanActive(plan: SubscriptionPlan) {
    try {
      await api.put(`/admin/subscription-plans/${plan.id}`, { is_active: !plan.is_active });
      setPlans((prev) =>
        prev.map((p) => (p.id === plan.id ? { ...p, is_active: !p.is_active } : p))
      );
      useUIStore.getState().addToast(`Plan ${plan.is_active ? 'deactivated' : 'activated'}`, 'success');
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to update plan', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="text-gray-600 mt-1">Manage recurring subscription offerings</p>
        </div>
        <Button variant="primary" size="md" onClick={openCreateModal}>
          <Plus className="w-5 h-5 mr-2" />
          Add Plan
        </Button>
      </div>

      {error && (
        <Card padding="md">
          <p className="text-red-600">{error}</p>
        </Card>
      )}

      {/* Guided Stripe setup — reflects live connection + sync status */}
      <SubscriptionBillingChecklist plans={plans} />

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No subscription plans yet</h3>
            <p className="text-gray-600 mb-6">Create your first subscription plan to start offering recurring access.</p>
            <Button variant="primary" size="md" onClick={openCreateModal}>
              <Plus className="w-5 h-5 mr-2" />
              Add Plan
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} padding="lg" className={!plan.is_active ? 'opacity-60' : ''}>
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={intervalVariants[plan.interval]}>
                        {intervalLabels[plan.interval]}
                      </Badge>
                      {!plan.is_active && <Badge variant="red">Inactive</Badge>}
                      {Number(plan.price) > 0 &&
                        (plan.stripe_price_id ? (
                          <Badge variant="green">Synced to Stripe</Badge>
                        ) : (
                          <Badge variant="yellow">Not synced</Badge>
                        ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{formatCurrency(plan.price)}</div>
                    {plan.compare_at_price != null && (
                      <div className="text-sm text-gray-400 line-through">
                        {formatCurrency(plan.compare_at_price)}
                      </div>
                    )}
                    <div className="text-xs text-gray-500">/{plan.interval}</div>
                  </div>
                </div>

                {plan.description && (
                  <p className="text-sm text-gray-600">{plan.description}</p>
                )}

                {Array.isArray(plan.features) && plan.features.length > 0 && (
                  <ul className="space-y-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">&#10003;</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex items-center gap-4 text-sm text-gray-500 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {plan.subscriber_count} subscribers
                  </div>
                  {plan.courses_included != null && (
                    <span>
                      {plan.courses_included === 0 ? 'All courses' : `${plan.courses_included} courses`}
                    </span>
                  )}
                  <span>Tier {plan.tier_level}</span>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button variant="secondary" size="sm" onClick={() => openEditModal(plan)} className="flex-1">
                    <Pencil className="w-3.5 h-3.5 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePlanActive(plan)}
                  >
                    {plan.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  {!plan.is_active && plan.subscriber_count === 0 && (
                    <Button variant="ghost" size="sm" onClick={() => openDeleteModal(plan.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={editingPlanId ? 'Edit Subscription Plan' : 'New Subscription Plan'}
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <Input
            label="Plan Name"
            value={formData.name}
            onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Pro Monthly"
          />
          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
            rows={3}
            placeholder="Plan description..."
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Billing Interval"
              value={formData.interval}
              onChange={(e) => setFormData((f) => ({ ...f, interval: e.target.value as any }))}
              options={[
                { label: 'Monthly', value: 'monthly' },
                { label: 'Annual', value: 'annual' },
              ]}
            />
            <Input
              label="Price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData((f) => ({ ...f, price: e.target.value }))}
              placeholder="0.00"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Compare at Price"
              type="number"
              value={formData.compare_at_price}
              onChange={(e) => setFormData((f) => ({ ...f, compare_at_price: e.target.value }))}
              placeholder="0.00"
            />
            <Input
              label="Tier Level"
              type="number"
              value={formData.tier_level}
              onChange={(e) => setFormData((f) => ({ ...f, tier_level: e.target.value }))}
              placeholder="1"
            />
          </div>
          <Input
            label="Courses Included"
            type="number"
            value={formData.courses_included}
            onChange={(e) => setFormData((f) => ({ ...f, courses_included: e.target.value }))}
            placeholder="Leave empty for unlimited"
            helperText="Number of courses included. Leave empty for unlimited."
          />

          {/* Features */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Features</label>
            <div className="space-y-2">
              {formData.features.map((feature, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-gray-700 bg-gray-50 px-3 py-1.5 rounded-lg">
                    {feature}
                  </span>
                  <button
                    onClick={() => removeFeature(i)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="Add a feature..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addFeature();
                    }
                  }}
                />
                <Button variant="secondary" size="sm" onClick={addFeature}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData((f) => ({ ...f, is_active: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button variant="secondary" size="sm" onClick={() => setFormModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isSaving ? 'Saving...' : editingPlanId ? 'Update Plan' : 'Create Plan'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Confirmation */}
      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Deactivate Plan">
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to deactivate this plan? Existing subscribers will not be affected,
            but no new subscriptions can be created.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleDeactivate} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isDeleting ? 'Deactivating...' : 'Deactivate Plan'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
